import { Dialog } from "primereact/dialog";
import {
  useLocalUser,
  useSafeReports,
  useUsers,
} from "../../pages/specialty-assistant/data";
import { downloadReportHistory, LOB } from "../../api/routes";
import { export_format } from "../../client";
import styles from "./ExportReportModal.module.css";
import { Checkbox } from "primereact/checkbox";
import { RadioButton } from "primereact/radiobutton";
import { useMemo, useState } from "react";
import { clone } from "lodash";
import {
  AssigneeFilterDropdown,
  FilterDropdown,
  FilterOption,
  StatusFilterDropdown,
  UNASSIGNED_USER,
} from "../filters/Filters";
import {
  getAllStatuses,
  numericAppetiteScore,
  readableAppetiteFromScore,
  readableStatus,
} from "../../pages/specialty-assistant/dashboard/util";
import { useClientId } from "../../ClientConfig";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { downloadBlob, isAutomationAccount } from "../../util";
import { lobName } from "../../pages/specialty-assistant/dashboard/lob";
import { perClient } from "../../client-features";

type ExportReportModalProps = {
  visible: boolean;
  setVisible: (value: boolean) => void;
};

const filtersByClient = perClient({
  luminor: {
    appetite: false,
  },
});

export const ExportReportModal: React.FC<ExportReportModalProps> = ({
  visible,
  setVisible,
}) => {
  const user = useLocalUser();
  const clientId = useClientId();
  const [isLoading, setIsLoading] = useState(false);
  const [byLocation, setByLocation] = useState(false);
  const [exportFormat, setExportFormat] = useState<export_format>(
    export_format.XLSX
  );
  const allLobs: LOB[] = (user?.lobs ?? []).map((lob) => lob as LOB);
  const allReportsRequest = useSafeReports(allLobs);
  const allReports = allReportsRequest.data ?? [];

  const [lobsChecked, setLobsChecked] = useState(allLobs);

  const statuses = getAllStatuses().map((status) => {
    return {
      name: readableStatus(status),
      code: status,
    };
  });
  const [selectedStatuses, setSelectedStatuses] = useState(statuses);

  const appetites = [1, 2, 3, 5].map((appetite) => {
    return {
      name: readableAppetiteFromScore(appetite),
      code: appetite,
    };
  });
  const [selectedAppetites, setSelectedAppetites] =
    useState<FilterOption<number>[]>(appetites);

  const users = useUsers().data;
  const [selectedUsers, setSelectedUsers] = useState([
    ...(users?.all_company_users ?? [])
      .filter((user) => !isAutomationAccount(user))
      .map((user) => {
        return { id: user.id, name: user.full_name ?? "" };
      }),
    UNASSIGNED_USER,
  ]);

  const [submissionDates, setSubmissionDates] = useState<
    (Date | null)[] | null | undefined
  >(null);
  const [effectiveDates, setEffectiveDates] = useState<
    (Date | null)[] | null | undefined
  >(null);

  const enabledFeatures = filtersByClient[clientId];

  const filteredReports = useMemo(() => {
    let filtered = allReports;

    // Filter LOB
    filtered = filtered.filter((report) =>
      lobsChecked.includes(report.lob as LOB)
    );

    // Filter by status
    filtered = filtered.filter((report) =>
      selectedStatuses.some((status) => status.code === report.status)
    );

    if (enabledFeatures?.appetite) {
      // Filter by appetite
      filtered = filtered.filter((report) =>
        selectedAppetites.some(
          (appetite) =>
            report.report_metadata.precomputed_metadata.appetite &&
            appetite.code ===
              numericAppetiteScore(
                report.report_metadata.precomputed_metadata.appetite
                  .appetite_result
              )
        )
      );
    }

    // Filter by users
    filtered = filtered.filter((report) =>
      selectedUsers.some(
        (user) =>
          (user.id === "Unassigned" && !report.assignee) ||
          user.id === report.assignee
      )
    );

    // Filter by creation date
    const filteredSubmissionDates = (submissionDates ?? []).filter(
      (date) => date !== null
    ) as Date[];
    if (filteredSubmissionDates.length > 1) {
      // If we have a start and end date
      const startDate = filteredSubmissionDates[0]!;
      const endDate = filteredSubmissionDates[1]!;

      // Set end date to end of day (23:59:59.999) to make it inclusive
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter((report) => {
        const reportDate = new Date(report.created_at_ts + "Z");
        return reportDate >= startDate && reportDate <= endDate;
      });
    }

    // Filter by effective date
    const filteredEffectiveDates = (effectiveDates ?? []).filter(
      (date) => date !== null
    ) as Date[];

    if (filteredEffectiveDates.length > 1) {
      // If we have a start and end date
      const startDate = new Date(filteredEffectiveDates[0]!);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(filteredEffectiveDates[1]!);
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter((report) => {
        // Parse mm/dd/yyyy format
        const parts =
          report.report_metadata.company_info.effective_date.split("/");

        if (parts.length !== 3) {
          return false;
        }

        const [month, day, year] = parts.map(Number);
        const reportDate = new Date(year!, month! - 1, day); // month is 0-indexed
        return reportDate >= startDate && reportDate <= endDate;
      });
    }

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [
    lobsChecked,
    selectedAppetites,
    selectedUsers,
    selectedStatuses,
    submissionDates,
    effectiveDates,
    allReports,
  ]);

  return (
    <Dialog
      header="Export Report"
      visible={visible}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
      onHide={() => setVisible(false)}
    >
      <div className={styles.verticalContainer}>
        <span>
          Submissions selected:{" "}
          <b style={{ color: "var(--primary-color)" }}>
            {!allReportsRequest.isPending
              ? filteredReports.length
              : "Loading..."}
          </b>
        </span>
        <div className={styles.group}>
          <span className={styles.header}>Export Type</span>
          <div className={styles.horizontalContainer}>
            <Checkbox
              checked={byLocation}
              onChange={(e) => setByLocation(!!e.checked)}
            />
            <div>By Location</div>
          </div>
        </div>
        <div className={styles.group}>
          <span className={styles.header}>Format</span>
          <div className={styles.horizontalContainer}>
            <RadioButton
              inputId="export-format-xlsx"
              checked={exportFormat === export_format.XLSX}
              onChange={() => setExportFormat(export_format.XLSX)}
            />
            <label htmlFor="export-format-xlsx">XLSX</label>
          </div>
          <div className={styles.horizontalContainer}>
            <RadioButton
              inputId="export-format-csv"
              checked={exportFormat === export_format.CSV}
              onChange={() => setExportFormat(export_format.CSV)}
            />
            <label htmlFor="export-format-csv">CSV</label>
          </div>
          {exportFormat === export_format.CSV ? (
            <span className={styles.hint}>
              CSV is more memory efficient and has no report limit.
            </span>
          ) : null}
        </div>
        <div className={styles.group}>
          <span className={styles.header}>Line of Business</span>
          {allLobs.map((lob) => {
            return (
              <div className={styles.horizontalContainer} key={lob}>
                <Checkbox
                  checked={lobsChecked.includes(lob)}
                  onChange={() =>
                    setLobsChecked((previousLobs) => {
                      let newLobs = clone(previousLobs);
                      if (newLobs.includes(lob)) {
                        newLobs = newLobs.filter(
                          (otherLob) => otherLob !== lob
                        );
                      } else {
                        newLobs.push(lob);
                      }
                      return newLobs;
                    })
                  }
                />
                <div>{lobName[lob]}</div>
              </div>
            );
          })}
        </div>
        <div className={styles.group}>
          <span className={styles.header}>Submission Status</span>
          <StatusFilterDropdown
            statuses={statuses}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            isTransparent={false}
          />
        </div>
        {enabledFeatures?.appetite ? (
          <div className={styles.group}>
            <span className={styles.header}>Appetite</span>
            <FilterDropdown
              options={appetites}
              selectedOptions={selectedAppetites}
              setSelectedOptions={setSelectedAppetites}
              isTransparent={false}
            />
          </div>
        ) : null}
        <div className={styles.group}>
          <span className={styles.header}>Assignee</span>
          <AssigneeFilterDropdown
            icon={"pi pi-user-edit"}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            isTransparent={false}
          />
        </div>
        <div className={styles.group}>
          <span className={styles.header}>Submission Date Range</span>
          <Calendar
            value={submissionDates}
            onChange={(e) => setSubmissionDates(e.value)}
            selectionMode="range"
            readOnlyInput
            style={{ width: "260px" }}
          />
        </div>
        <div className={styles.group}>
          <span className={styles.header}>Effective Date Range</span>
          <Calendar
            value={effectiveDates}
            onChange={(e) => setEffectiveDates(e.value)}
            selectionMode="range"
            readOnlyInput
            style={{ width: "260px" }}
          />
        </div>
        <Button
          style={{ marginTop: "16px" }}
          loading={isLoading}
          label="Export report history"
          onClick={async () => {
            try {
              setIsLoading(true);
              const blob = await downloadReportHistory(
                filteredReports.map((report) => report.report_id),
                byLocation ? "complex_location" : "account",
                exportFormat
              );
              const dateString = new Date()
                .toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
                .replace(/\//g, "-");
              const filename = `Report History ${dateString}.${exportFormat}`;
              downloadBlob(filename, blob);
            } finally {
              setIsLoading(false);
            }
          }}
        />
      </div>
    </Dialog>
  );
};
